/**
 * Hook dữ liệu của màn Truy vấn mẫu (khuôn nghiệp vụ). View và thành phần chỉ gọi hook ở đây.
 *
 * Danh sách khuôn của một nguồn dùng khoá `KHOA.khuon(sourceId)` cho cả lưới nguồn lẫn màn chi tiết: bấm vào thẻ là màn
 * chi tiết có sẵn dữ liệu. Đối chiếu chế độ nằm dưới cùng nhánh, nên một lần vô hiệu hoá làm mới cả hai.
 */
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';

import { khoTroLy } from '../../../lib/repositories/tro-ly-ai.repository';
import type { CheDoKhai, GraphqlSource, KhuonInput, NhomKhuon } from '../types';
import { KHOA, thuLai } from './useChung';

const KHUON_STALE_MS = 30_000;

const khoaDoiChieu = (sourceId: string) => [...KHOA.khuon(sourceId), 'che-do'] as const;

export function useDanhSachKhuon(sourceId: string) {
  return useQuery({
    queryKey: KHOA.khuon(sourceId),
    queryFn: () => khoTroLy.danhSachKhuon(sourceId),
    enabled: !!sourceId,
    staleTime: KHUON_STALE_MS,
    retry: thuLai,
  });
}

/**
 * Số khuôn của từng nguồn cho chân thẻ ở lưới. Lỗi ở một nguồn chỉ làm thẻ ấy thiếu số đếm, không làm hỏng cả lưới.
 */
export function useDemKhuonTheoNguon(ds: GraphqlSource[]) {
  return useQueries({
    queries: ds.map((s) => ({
      queryKey: KHOA.khuon(s.id),
      queryFn: () => khoTroLy.danhSachKhuon(s.id),
      staleTime: KHUON_STALE_MS,
      retry: thuLai,
    })),
  });
}

/** Đối chiếu trước khi gạt công tắc — chỉ hỏi khi hộp thoại mở. */
export function useDoiChieuCheDo(sourceId: string, bat: boolean) {
  return useQuery({
    queryKey: khoaDoiChieu(sourceId),
    queryFn: () => khoTroLy.doiChieuCheDo(sourceId),
    enabled: !!sourceId && bat,
    staleTime: 0,
    retry: thuLai,
  });
}

/** Mọi thao tác ghi của màn khuôn một nguồn. Mỗi lần ghi xong làm mới danh sách khuôn (và đối chiếu chế độ). */
export function useThaoTacKhuon(sourceId: string) {
  const qc = useQueryClient();
  const lamMoi = () => qc.invalidateQueries({ queryKey: KHOA.khuon(sourceId) });

  const luuNhom = useMutation({
    mutationFn: (v: { nhom: NhomKhuon | null; ten: string }) =>
      // PUT thay cả dòng: gửi mỗi `ten` là xoá mô tả và đưa nhóm về đầu danh sách — nên giữ cả `moTa`/`thuTu`.
      khoTroLy.luuNhomKhuon(sourceId, v.nhom ? { ...v.nhom, ten: v.ten } : { ten: v.ten }),
    onSuccess: lamMoi,
  });

  const xoaNhom = useMutation({
    mutationFn: (id: string) => khoTroLy.xoaNhomKhuon(sourceId, id),
    onSuccess: lamMoi,
  });

  const luu = useMutation({
    mutationFn: (than: KhuonInput) => khoTroLy.luuKhuon(sourceId, than),
    onSuccess: lamMoi,
  });

  const xoa = useMutation({
    mutationFn: (id: string) => khoTroLy.xoaKhuon(sourceId, id),
    onSuccess: lamMoi,
  });

  const chayThu = useMutation({
    mutationFn: (v: { id: string; thamSo: Record<string, string> }) => khoTroLy.chayThuKhuon(sourceId, v.id, v.thamSo),
    onSuccess: lamMoi,
  });

  const datCheDo = useMutation({
    mutationFn: (c: CheDoKhai) => khoTroLy.datCheDo(sourceId, c),
    onSuccess: () => {
      void lamMoi();
      // Thẻ nguồn và danh sách nguồn ở Kho dữ liệu, Kết nối nguồn đọc `cheDo` từ đây.
      void qc.invalidateQueries({ queryKey: KHOA.nguon });
    },
  });

  /**
   * Khai lại ĐÚNG những bảng đang thiếu ở bản khai. `giuLoiKhai: true` KHÔNG phải tuỳ chọn: thiếu nó thì nhãn cột,
   * nghĩa mã và `grain` đã khai bị thay bằng bản đề xuất trắng. Chỉ gửi bảng ĐANG THIẾU — khai lại một bảng không liên
   * quan là đẩy nó qua đường dò lược đồ cho một việc không ai yêu cầu.
   *
   * Trả về số bảng đã gửi; `0` nghĩa là nguồn không còn bảng nào trùng tên (bị đổi tên hoặc xoá).
   */
  const capNhatBanKhai = useMutation({
    mutationFn: async (bangThieu: string[]) => {
      const kq = await khoTroLy.doNguon(sourceId);
      const can = new Set(bangThieu.map((x) => x.toLowerCase()));
      const goi = (kq.tables ?? [])
        .filter((b) => can.has(String(b.suggestion?.name ?? '').toLowerCase()))
        .map((b) => b.suggestion as Record<string, unknown>);
      if (goi.length) await khoTroLy.luuBang(sourceId, goi, true);
      return goi.length;
    },
    onSuccess: (so) => {
      if (so) void qc.invalidateQueries({ queryKey: KHOA.nguonChiTiet(sourceId) });
    },
  });

  return { luuNhom, xoaNhom, luu, xoa, chayThu, datCheDo, capNhatBanKhai };
}
